import React, { useState } from 'react';
import { Tabs, Tab, Box, Typography, RadioGroup, FormControlLabel, Radio, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { Download, Info, CheckCircle } from '@mui/icons-material'; // <-- Add CheckCircle
import SpinnerModal from './SpinnerModal';
import jsPDF from 'jspdf';


const Scheduler = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [creationType, setCreationType] = useState('');
  const [instructions, setInstructions] = useState('');
  const [progress, setProgress] = useState(0);
  const [showSpinnerModal, setShowSpinnerModal] = useState(false);
  const [spinnerType, setSpinnerType] = useState(null);
  const [apiResult, setApiResult] = useState(null);

  // Spinner messages based on creationType
  const getSpinnerMessages = () => {
    if (spinnerType === 'stepfunctions') {
      return {
        inProgress: 'Step Function is being generated...',
        success: 'Step Function generated Successfully!',
        open: showSpinnerModal,
        onClose: () => setShowSpinnerModal(false),
      };
    } else if (spinnerType === 'dag') {
      return {
        inProgress: 'DAG is being generated...',
        success: 'DAG generated Successfully!',
        open: showSpinnerModal,
        onClose: () => setShowSpinnerModal(false),
      };
    }
    return {};
  };

  const spinnerMessages = getSpinnerMessages();

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Submit handler for scheduler_task_process endpoint
  const handleSubmit = async (instructionString) => {
    // Set spinner type and show spinner
    setSpinnerType(creationType.toLowerCase() === 'stepfunctions' ? 'stepfunctions' : 'dag');
    setShowSpinnerModal(true);
    setProgress(0);

    // Simulate progress
    let interval = null;
    interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 8000);

    const payload = {
      type_of_code: creationType,
      user_input: instructionString, // Use the latest value
    };

    try {
      const response = await fetch('https://api.elevate.de.ltim.io/scheduler_task_process/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      clearInterval(interval);
      setProgress(100);

      const result = await response.json();
      setApiResult(result);
      setTimeout(() => {
        setShowSpinnerModal(false);
        setTabIndex(1);
      }, 1500);
    } catch (error) {
      clearInterval(interval);
      setShowSpinnerModal(false);
      setProgress(0);
      alert('An error occurred while processing the request.');
      console.error(error);
    }
  };

  // Download PDF handler
  const handleDownloadPDF = () => {
    if (!apiResult || !apiResult.report) {
      alert('No API result available to download.');
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Output', 10, 10);

    const resultContent = String(apiResult.report);
    doc.setFontSize(11);
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;
    const lines = doc.splitTextToSize(resultContent, contentWidth);
    let cursorY = 20;
    const lineHeight = 7;
    const pageHeight = doc.internal.pageSize.height;

    lines.forEach((line) => {
      if (cursorY + lineHeight > pageHeight - margin) {
        doc.addPage();
        cursorY = margin;
      }
      doc.text(line, margin, cursorY);
      cursorY += lineHeight;
    });

    doc.save('Output.pdf');
  };

  const dagFields = [
    { key: 'dagName', label: 'DAG Name', description: 'Unique identifier for the DAG', example: 'daily_etl_pipeline' },
    { key: 'schedule', label: 'Schedule', description: 'Cron expression or Airflow preset or Natural Language', example: '@daily, 0 12 * * *, Or natural language like the DAG should run daily at 8:00 AM EST' },
    { key: 'startDate', label: 'Start Date', description: 'When the DAG should start running', example: '2025-05-01' },
    { key: 'tasks', label: 'Tasks', description: 'List of tasks with details', example: 'extract, transform, load' },
    { key: 'dependencies', label: 'Dependencies', description: 'Task execution order', example: 'extract >> transform >> load' },
    { key: 'retries', label: 'Retries (optional)', description: 'Number of retries on failure', example: '3' },
    { key: 'retryDelay', label: 'Retry Delay (optional)', description: 'Time between retries', example: '5 minutes' },
  ];

  const stepFunctionFields = [
    { key: 'stateMachineName', label: 'State Machine Name', description: 'Unique name for the workflow', example: 'ETLStateMachine' },
    { key: 'states', label: 'States', description: 'List of steps with details', example: 'ExtractData, TransformData, Etc' },
    { key: 'startState', label: 'Start State', description: 'Name of the first state', example: 'ExtractData' },
    { key: 'transitions', label: 'Transitions', description: 'Define what comes next after each state', example: 'ExtractData → TransformData → LoadData' },
    { key: 'errorHandling', label: 'Error Handling', description: 'Retry and catch logic', example: 'Retry on Lambda.ServiceException' },
    { key: 'timeouts', label: 'Timeouts (optional)', description: 'Max time for each state', example: '300 seconds' },
  ];

  const [tableData, setTableData] = useState({});

  return (
    <Box sx={{ width: '100%', marginTop: -7 }}>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{
          pl: 0, // Remove left padding from Tabs
          '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' }, // Align tabs to left
          '& .MuiTab-root': {
            minWidth: 120,
            fontWeight: 'bold',
            textTransform: 'none',
            paddingRight: 0,
            pl: 0, // Remove left padding from Tab
          },
        }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 1 Input */}
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={24}
                  height={24}
                  borderRadius="50%"
                  bgcolor={apiResult?.report ? "#4caf50" : (tabIndex === 0 ? "#1976d2" : "white")}
                  border="1.5px solid"
                  borderColor={apiResult?.report ? "#4caf50" : (tabIndex === 0 ? "#1976d2" : "black")}
                  mr={1}
                  transition="all 0.2s"
                >
                  {apiResult?.report ? (
                    <CheckCircle sx={{ color: "white", fontSize: 20 }} />
                  ) : (
                    <Typography variant="subtitle2" sx={{ color: tabIndex === 0 ? "white" : "black", fontWeight: 600 }}>1</Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontWeight: tabIndex === 0 ? 700 : 400,
                    color: tabIndex === 0 ? "primary.main" : "black",
                    userSelect: "none",
                    transition: "all 0.2s"
                  }}
                >
                  Input
                </Typography>
              </Box>
              {/* Arrow - vertically centered with the circles and text */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 4, // match the fontSize for vertical centering
                  ml: 1,
                  mr: 1,
                  mb: 0.6, // Adjust for vertical centering
                }}
              >
                <Typography sx={{ color: "#bdbdbd", fontWeight: 700, fontSize: 44, lineHeight: 1 }}>
                  &rarr;
                </Typography>
              </Box>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingRight: 0, paddingLeft: 0 }}
        />
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 2 Result */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={24}
                height={24}
                borderRadius="50%"
                bgcolor={tabIndex === 1 ? "#1976d2" : "white"}
                border="1.5px solid"
                borderColor={tabIndex === 1 ? "#1976d2" : "black"}
                mr={1}
                transition="all 0.2s"
              >
                <Typography variant="subtitle2" sx={{ color: tabIndex === 1 ? "white" : "black", fontWeight: 600 }}>2</Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: tabIndex === 1 ? 700 : 400,
                  color: tabIndex === 1 ? "primary.main" : "black",
                  userSelect: "none",
                  transition: "all 0.2s"
                }}
              >
                Result
              </Typography>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingLeft: 0 }}
        />
      </Tabs>
      {/* Input Content */}
      {tabIndex === 0 && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
            What do you want to create?
          </Typography>
          <RadioGroup
            row
            value={creationType}
            onChange={(event) => {
              setCreationType(event.target.value);
              setInstructions(''); // Reset instructions when type changes
            }}
          >
            <FormControlLabel value="Stepfunctions" control={<Radio />} label="Step Functions" />
            <FormControlLabel value="DAG" control={<Radio />} label="DAG" />
          </RadioGroup>
          {creationType && (
            <Box sx={{ marginTop: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                <Typography sx={{ fontWeight: 'bold', marginRight: 1 }}>
                  Stepwise Instruction for creating the {creationType}
                </Typography>
                <Tooltip
                  title={
                    <span>
                      Provide a brief description of your workflow's purpose (e.g., "processing daily sales data").<br />
                      List all tasks that need to be executed. Include specific details for each task such as database types, stored procedure names, or function details.<br />
                      Define the dependencies between tasks. Clearly state which tasks must run after others and any conditional execution requirements.<br />
                      Specify when and how often the workflow should run (e.g., "Daily at 2 AM").<br />
                      Describe your error handling requirements. Include retry policies and how failures should be handled or reported.<br />
                      Add any additional requirements such as logging needs, metadata handling, or other special considerations.<br />
                      Be as specific and detailed as possible in your requirements to ensure the generated code meets your needs exactly.
                    </span>
                  }
                  placement="right"
                  arrow
                >
                  <Info sx={{ color: 'gray', cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              <TableContainer component={Paper} sx={{ width: '90%', marginBottom: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Input</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Example</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(creationType === 'DAG' ? dagFields : stepFunctionFields).map((field) => (
                      <TableRow key={field.key}>
                        <TableCell>{field.label}</TableCell>
                        <TableCell>{field.description}</TableCell>
                        <TableCell sx={{ fontStyle: 'italic', color: 'gray' }}>{field.example}</TableCell>
                        <TableCell>
                          <TextField
                            variant="standard"
                            value={tableData[field.key] || ''}
                            onChange={(e) =>
                              setTableData((prev) => ({ ...prev, [field.key]: e.target.value }))
                            }
                            fullWidth
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    marginTop: 2,
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid black',
                    boxShadow: 'none',
                    height: '40px',
                  }}
                  onClick={() => {
                    const fields = creationType === 'DAG' ? dagFields : stepFunctionFields;
                    const instructionString = fields
                      .map((field) => `${field.label}: ${tableData[field.key] || ''}`)
                      .join('\n');
                    setInstructions(instructionString); // Optional: keep for display
                    handleSubmit(instructionString);    // Pass latest value directly
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Result Content */}
      {tabIndex === 1 && (
        <>
          <Box
            sx={{
              marginTop: 4,
              padding: '16px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #ccc',
              marginBottom: 2,
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Output
            </Typography>
            {apiResult?.report ? (
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
              >
                {String(apiResult.report)}
              </Typography>
            ) : (
              <Typography variant="body2">No result available</Typography>
            )}
          </Box>
          {apiResult?.report && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPDF}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
              startIcon={<Download />}
            >
              Download PDF
            </Button>
          )}
        </>
      )}

      {/* Spinner Modal */}
      <SpinnerModal
        open={spinnerMessages.open}
        progress={progress}
        inProgressMessage={spinnerMessages.inProgress}
        successMessage={spinnerMessages.success}
        onClose={spinnerMessages.onClose}
      />
    </Box>
  );
};

export default Scheduler;